import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { getWishlist, removeFromWishlist } from '../services/wishlistService';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import CloudflareImage from '../components/ui/cloudflare-image';
import PageLayout from '../components/PageLayout';

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  padding: 40px 16px;
  
  @media (min-width: 768px) {
    padding: 40px 24px;
  }
  
  @media (min-width: 1024px) {
    padding: 40px 24px;
  }
  
  @media (min-width: 1536px) {
    padding-left: 0;
    padding-right: 0;
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 20px;
  
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (min-width: 1280px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const ProductCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ProductImageContainer = styled.div`
  width: 100%;
  aspect-ratio: 1;
  background-color: #f5f5f5;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ProductInfoContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 0;
  position: relative;
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const ProductName = styled.h3`
  font-size: 14px;
  font-weight: 500;
  margin: 0 0 8px 0;
  text-transform: uppercase;
  color: #000;
`;

const ProductPrice = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const OriginalPrice = styled.span`
  font-size: 14px;
  color: #888;
  text-decoration: line-through;
`;

const CurrentPrice = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #000;
`;

const WishlistButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-left: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeartIcon = styled.svg`
  width: 24px;
  height: 24px;
  fill: #000;
  stroke: #000;
  stroke-width: 2;
`;

const EmptyWishlist = styled.div`
  text-align: center;
  padding: 60px 0;
`;

const EmptyMessage = styled.p`
  font-size: 18px;
  margin-bottom: 20px;
  color: #666;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 60px 0;
  font-size: 18px;
  color: #666;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 60px 0;
  font-size: 18px;
  color: #e53e3e;
`;

const ShopButton = styled(Link)`
  display: inline-block;
  background-color: #000;
  color: #fff;
  padding: 12px 24px;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
`;

const WishlistPage = () => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated()) {
      toast.info('Please log in to view your wishlist');
      navigate('/login');
      return;
    }

    const fetchWishlist = async () => {
      try {
        setLoading(true);
        const response = await getWishlist();
        if (response.success) {
          setWishlistItems(response.wishlist);
        } else {
          setError('Failed to fetch wishlist');
        }
      } catch (err) {
        setError(err.toString());
      } finally {
        setLoading(false);
      }
    };

    fetchWishlist();
  }, [isAuthenticated, navigate]);

  const handleRemoveFromWishlist = async (productId) => {
    try {
      const response = await removeFromWishlist(productId);
      if (response.success) {
        setWishlistItems(prevItems => prevItems.filter(item => item._id !== productId));
        toast.success('Item removed from wishlist');
      } else {
        toast.error('Failed to remove item');
      }
    } catch (err) {
      toast.error(err.toString());
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <ContentWrapper>
          <LoadingMessage>Loading your wishlist...</LoadingMessage>
        </ContentWrapper>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout>
        <ContentWrapper>
          <ErrorMessage>{error}</ErrorMessage>
        </ContentWrapper>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <ContentWrapper>
        {wishlistItems.length > 0 ? (
          <ProductGrid>
            {wishlistItems.map(item => (
              <ProductCard key={item._id}>
                <Link to={`/product/${item.slug}`} style={{ textDecoration: 'none' }}>
                  <ProductImageContainer>
                    <CloudflareImage src={item.images[0]?.url} alt={item.title} />
                  </ProductImageContainer>
                </Link>
                <ProductInfoContainer>
                  <ProductInfo>
                    <ProductName>{item.title}</ProductName>
                    <ProductPrice>
                      {item.originalPrice && <OriginalPrice>£{item.originalPrice}</OriginalPrice>}
                      <CurrentPrice>£{item.price}</CurrentPrice>
                    </ProductPrice>
                  </ProductInfo>
                  <WishlistButton onClick={() => handleRemoveFromWishlist(item._id)}>
                    <HeartIcon 
                      viewBox="0 0 24 24" 
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                    </HeartIcon>
                  </WishlistButton>
                </ProductInfoContainer>
              </ProductCard>
            ))}
          </ProductGrid>
        ) : (
          <EmptyWishlist>
            <EmptyMessage>Your wishlist is empty.</EmptyMessage>
            <ShopButton to="/shop">Continue Shopping</ShopButton>
          </EmptyWishlist>
        )}
      </ContentWrapper>
    </PageLayout>
  );
};

export default WishlistPage;
